/**
 * Idempotently convert Firestore-serialized timestamps to epoch millis on
 * leaderboard score responses. Leaderboard endpoints serialize Firestore
 * Timestamps as `{ seconds, nanoseconds }` (or `{ _seconds, _nanoseconds }`),
 * but `LeaderboardEntry` declares `submittedAt`/`expiresAt` as epoch millis.
 * Already-millis values pass through unchanged.
 */
function toMillis(value: unknown): unknown {
  if (value && typeof value === 'object') {
    const v = value as Record<string, unknown>
    // Firestore Timestamp instance (defensive — survives only in-process, not
    // over HTTP/JSON, but keeps parity with the RN host's normalizer so this can
    // be the single source of truth when that migrates).
    if (typeof v.toMillis === 'function') {
      try {
        return (v.toMillis as () => number)()
      } catch {
        return value
      }
    }
    const seconds = (v.seconds ?? v._seconds) as number | undefined
    const nanos = (v.nanoseconds ?? v._nanoseconds) as number | undefined
    if (typeof seconds === 'number' && typeof nanos === 'number') {
      return seconds * 1000 + Math.floor(nanos / 1_000_000)
    }
  }
  return value
}

function normalizeEntry(entry: unknown): unknown {
  if (!entry || typeof entry !== 'object') return entry
  const e = entry as Record<string, unknown>
  return { ...e, submittedAt: toMillis(e.submittedAt), expiresAt: toMillis(e.expiresAt) }
}

export function normalizeScoresResponse<T>(payload: T): T {
  if (!payload || typeof payload !== 'object') return payload
  const p = payload as Record<string, unknown>
  const result: Record<string, unknown> = { ...p }
  if (Array.isArray(p.entries)) result.entries = p.entries.map(normalizeEntry)
  if (p.context && typeof p.context === 'object') {
    const c = p.context as Record<string, unknown>
    result.context = {
      ...c,
      topEntries: Array.isArray(c.topEntries) ? c.topEntries.map(normalizeEntry) : c.topEntries,
      beforePlayer: Array.isArray(c.beforePlayer) ? c.beforePlayer.map(normalizeEntry) : c.beforePlayer,
      playerEntry: c.playerEntry ? normalizeEntry(c.playerEntry) : c.playerEntry,
      afterPlayer: Array.isArray(c.afterPlayer) ? c.afterPlayer.map(normalizeEntry) : c.afterPlayer,
    }
  }
  return result as T
}
